import { ApiOutlined, ArrowRightOutlined, BellOutlined, CloudServerOutlined, DatabaseOutlined, FieldTimeOutlined, FileTextOutlined, GlobalOutlined, LayoutOutlined, MailOutlined, MessageOutlined, RobotOutlined, UserOutlined, WalletOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { Descriptions, Tag } from 'antd';
import { api } from '../api';
import { Bone, ErrorNotice, PageHeader, Panel } from '../components';
import { timeAgo, useLoad } from '../lib';
import { AdminUsersSettings } from './settings/AdminUsersSettings';
import { AiSettings } from './settings/AiSettings';
import { BackToSettings } from './settings/BackToSettings';
import { PaymentSettings } from './settings/PaymentSettings';
import { RemoteHostSettings } from './settings/RemoteHostSettings';
import { EmailSettings } from './settings/EmailSettings';
import { SmsSettings } from './settings/SmsSettings';
import { WhatsAppSettings } from './settings/WhatsAppSettings';
import { CloudStorageSettings } from './settings/StorageSettings';
import { WhiteBooksSettings } from './settings/WhiteBooksSettings';
import { TrialSettings } from './settings/TrialSettings';
import { WalletSettings } from './settings/WalletSettings';
import { WebsiteSettings } from './settings/WebsiteSettings';
import { LegalPagesSettings } from './settings/LegalPagesSettings';
import { EnquiryNotificationSettings } from './settings/EnquiryNotificationSettings';
/** Every settings group. A new group is one entry here plus its section component below. */
export const SETTING_GROUPS = [
    {
        key: 'admins',
        title: 'Admin users',
        description: 'Who can sign in to this admin console, and the mobile numbers they sign in with.',
        icon: <UserOutlined />,
        tone: 'rose',
        status: ({ admins }) => ({
            label: `${admins.active} active`,
            color: 'success',
            detail: admins.total > admins.active ? `${admins.total - admins.active} disabled` : `${admins.total} ${admins.total === 1 ? 'admin' : 'admins'}`
        })
    },
    {
        key: 'sms',
        title: 'SMS',
        description: 'Fast2SMS: the sign-in codes for this console, the account panel and the desktop app.',
        icon: <MessageOutlined />,
        tone: 'amber',
        status: ({ sms }) => sms.keyUnreadable
            ? { label: 'Key needs re-entering', color: 'warning', detail: 'Fast2SMS' }
            : sms.active
                ? { label: 'Codes by SMS', color: 'success', detail: `Fast2SMS · ${sms.route === 'otp' ? 'OTP' : sms.route === 'dlt' ? `DLT ${sms.senderId}` : 'Quick SMS'} route` }
                : { label: sms.enabled ? 'Not complete' : 'Off', color: sms.enabled ? 'warning' : 'default', detail: sms.enabled ? sms.missing : 'Codes are not sent by SMS' }
    },
    {
        key: 'whatsapp',
        title: 'WhatsApp',
        description: 'WhatsApp Web V2 and the OneSaas file upload key. The desktop sends invoices through this server only.',
        icon: <WhatsAppOutlined />,
        tone: 'green',
        status: ({ whatsapp }) => !whatsapp
            ? { label: 'Not set up', color: 'default', detail: 'WhatsApp Web V2' }
            : whatsapp.keyUnreadable
                ? { label: 'Key needs re-entering', color: 'warning', detail: 'WhatsApp Web V2' }
                : whatsapp.active
                    ? { label: 'On', color: 'success', detail: whatsapp.uploadReady ? 'Desktop sends through this server · file upload on' : 'Desktop sends through this server · set file upload for invoices' }
                    : { label: whatsapp.enabled ? 'Not complete' : 'Off', color: whatsapp.enabled ? 'warning' : 'default', detail: whatsapp.enabled ? whatsapp.missing : 'WhatsApp is not connected' }
    },
    {
        key: 'email',
        title: 'Email',
        description: 'SMTP: the registration codes for the account panel, sent to the customer\'s email.',
        icon: <MailOutlined />,
        tone: 'sky',
        status: ({ email }) => !email
            ? { label: 'Not set up', color: 'default', detail: 'SMTP' }
            : email.passwordUnreadable
                ? { label: 'Password needs re-entering', color: 'warning', detail: email.host || 'SMTP' }
                : email.active
                    ? { label: 'Codes by email', color: 'success', detail: `${email.host}:${email.port}` }
                    : { label: email.enabled ? 'Not complete' : 'Off', color: email.enabled ? 'warning' : 'default', detail: email.enabled ? email.missing : 'Codes are not sent by email' }
    },
    {
        key: 'payments',
        title: 'Payments',
        description: 'The Razorpay keys customers pay with, the webhook, GST and the details printed on invoices.',
        icon: <WalletOutlined />,
        tone: 'green',
        status: ({ payments }) => payments.secretUnreadable
            ? { label: 'Secret needs re-entering', color: 'warning', detail: payments.keyId }
            : payments.configured
                ? { label: 'Payments are on', color: 'success', detail: `${payments.keyId.startsWith('rzp_live_') ? 'Live' : 'Test'} key ${payments.keyId}` }
                : { label: 'Payments are off', color: 'default', detail: 'No Razorpay key yet' }
    },
    {
        key: 'storage',
        title: 'Cloud storage',
        description: 'The Backblaze B2 bucket that companies synced from the desktop app are kept in.',
        icon: <CloudServerOutlined />,
        tone: 'sky',
        status: ({ storage }) => storage.keyUnreadable
            ? { label: 'Key needs re-entering', color: 'warning', detail: storage.bucket }
            : storage.enabled
                ? { label: 'Sync on', color: 'success', detail: `Bucket ${storage.bucket}` }
                : { label: storage.bucket ? 'Sync off' : 'Not set up', color: 'default', detail: storage.bucket ? `Bucket ${storage.bucket}` : 'No bucket yet' }
    },
    {
        key: 'ai',
        title: 'AI reading',
        description: 'Claude for statements and invoices, plus Gemini (or another provider) for handwritten bill photos.',
        icon: <RobotOutlined />,
        tone: 'indigo',
        status: ({ ai, aiBill }) => {
            if (ai.keyUnreadable || aiBill?.keyUnreadable) return { label: 'Key needs re-entering', color: 'warning', detail: ai.model };
            const parts = [];
            if (ai.apiKeySet) parts.push('Statements');
            if (aiBill?.ready) parts.push('Bill photos');
            if (!parts.length) return { label: 'Not set up', color: 'default', detail: 'No API key yet' };
            return { label: 'On', color: 'success', detail: parts.join(' · ') };
        }
    },
    {
        key: 'wallet',
        title: 'Wallet & Usage',
        description: 'Central wallet funding rules, service prices, account overrides and metered usage.',
        icon: <WalletOutlined />,
        tone: 'green',
        status: () => ({ label: 'Centralized', color: 'success', detail: 'APIs, AI and future paid services' })
    },
    {
        key: 'whitebooks',
        title: 'WhiteBooks APIs',
        description: 'Separate production credentials and access for GST, e-Invoice and e-Way Bill.',
        icon: <ApiOutlined />,
        tone: 'sky',
        status: ({ whitebooks }) => ({
            label: whitebooks.enabled ? 'Provider on' : 'Provider off',
            color: whitebooks.enabled ? 'success' : 'default',
            detail: `${whitebooks.ready} of 3 production APIs ready`
        })
    },
    {
        key: 'website',
        title: 'Website',
        description: 'Public brand, homepage content, calls to action and visual theme.',
        icon: <LayoutOutlined />,
        tone: 'indigo',
        status: ({ website }) => ({ label: 'Managed', color: 'success', detail: website?.brand?.name || 'Kivonex' })
    },
    {
        key: 'legal',
        title: 'Legal pages',
        description: 'Privacy, terms, refund and delivery pages shown on the public website.',
        icon: <FileTextOutlined />,
        tone: 'slate',
        status: ({ legal }) => ({
            label: legal?.pages ? `${legal.pages} pages` : 'Managed',
            color: 'success',
            detail: legal?.updatedAt ? 'Saved in the database' : 'Kivonex defaults until you edit'
        })
    },
    {
        key: 'enquiry-notifications',
        title: 'Contact notifications',
        description: 'Who receives an email when the public contact form is submitted.',
        icon: <BellOutlined />,
        tone: 'amber',
        status: ({ enquiryNotifications }) => ({ label: enquiryNotifications?.enabled ? 'On' : 'Off', color: enquiryNotifications?.enabled ? 'success' : 'default', detail: `${enquiryNotifications?.recipients?.length ?? 0} recipients` })
    },
    {
        key: 'trial',
        title: 'Trial',
        description: 'How many days a new portal account may use Kivonex before it needs a plan.',
        icon: <FieldTimeOutlined />,
        tone: 'amber',
        status: ({ trial }) => ({
            label: trial?.days ? `${trial.days}-day trial` : 'No trial',
            color: trial?.days ? 'success' : 'default',
            detail: trial?.days ? 'New portal accounts' : 'New accounts must buy a plan'
        })
    },
    {
        key: 'remote',
        title: 'Remote hostnames',
        description: 'Blocked branded hostnames under kivonex.in. Customers cannot claim these names.',
        icon: <GlobalOutlined />,
        tone: 'indigo',
        status: ({ remote }) => ({ label: `${(remote?.names ?? []).length} blocked`, color: 'success', detail: `*.${remote?.domain ?? 'kivonex.in'}` })
    },
    {
        key: 'server',
        title: 'Server',
        description: 'The database and port this API runs with. Read only.',
        icon: <DatabaseOutlined />,
        tone: 'slate',
        status: ({ server }) => ({ label: 'Read only', color: 'default', detail: server.database ? `Database ${server.database}` : 'In-memory database' })
    }
];
export const SETTING_TITLES = Object.fromEntries(SETTING_GROUPS.map((group) => [group.key, group.title]));
export function SettingsPage({ go, section }) {
    if (section === 'admins')
        return <AdminUsersSettings />;
    if (section === 'storage')
        return <CloudStorageSettings />;
    if (section === 'ai')
        return <AiSettings />;
    if (section === 'payments')
        return <PaymentSettings />;
    if (section === 'sms')
        return <SmsSettings />;
    if (section === 'whatsapp')
        return <WhatsAppSettings />;
    if (section === 'email')
        return <EmailSettings />;
    if (section === 'server')
        return <ServerSettings />;
    if (section === 'whitebooks')
        return <WhiteBooksSettings />;
    if (section === 'wallet')
        return <WalletSettings />;
    if (section === 'remote')
        return <RemoteHostSettings />;
    if (section === 'trial')
        return <TrialSettings />;
    if (section === 'website')
        return <WebsiteSettings />;
    if (section === 'legal')
        return <LegalPagesSettings />;
    if (section === 'enquiry-notifications')
        return <EnquiryNotificationSettings />;
    return <SettingsIndex go={go}/>;
}
function SettingsIndex({ go }) {
    const { data, error, loading } = useLoad(async () => {
        const [overview, payments, providers, whitebooks, remote, website, enquiryNotifications, legal] = await Promise.all([
            api('/v1/admin/settings'),
            api('/v1/admin/settings/payments'),
            api('/v1/admin/gst/providers'),
            api('/v1/admin/gst/providers/authorized/config'),
            api('/v1/admin/settings/remote'),
            api('/v1/admin/settings/website'),
            api('/v1/admin/settings/enquiry-notifications'),
            api('/v1/admin/legal')
        ]);
        const provider = providers.providers.find((row) => row.id === 'authorized');
        const ready = Object.values(whitebooks.products ?? {}).filter((product) => product.health?.ok).length;
        return { ...overview, payments: payments.settings, remote: remote.settings, website: website.settings, enquiryNotifications: enquiryNotifications.settings, legal: { pages: legal.pages?.length ?? 0, updatedAt: legal.updatedAt }, whitebooks: { enabled: Boolean(provider?.enabled), ready }, trial: { days: website.settings?.trialDays ?? 0 } };
    });
        const changed = data ? [data.storage.updatedAt, data.ai.updatedAt, data.aiBill?.updatedAt, data.sms.updatedAt, data.whatsapp?.updatedAt, data.email?.updatedAt, data.payments.updatedAt].filter(Boolean).sort().at(-1) : undefined;
    return (<>
      <PageHeader title="Settings" description={<>
            How Kivonex Cloud is set up. Everything here is saved in the database and applies right away.
            {changed && <> Last change {timeAgo(changed)}.</>}
          </>}/>
      <ErrorNotice error={error}/>
      <div className="settings-grid">
        {SETTING_GROUPS.map((group) => {
            const status = data ? group.status(data) : null;
            return (<button key={group.key} type="button" className="panel setting-card" onClick={() => go('settings', group.key)}>
              <span className={`stat-icon tone-${group.tone}`}>{group.icon}</span>
              <span className="setting-card-text">
                <span className="setting-card-title">
                  {group.title}
                  {status ? (<Tag color={status.color} variant="filled" style={{ marginInlineStart: 8 }}>
                      {status.label}
                    </Tag>) : (loading && <Bone width={64} height={20} style={{ display: 'inline-block', marginInlineStart: 8, verticalAlign: -4, borderRadius: 6 }}/>)}
                </span>
                <span className="setting-card-desc">{group.description}</span>
                <span className="cell-sub">{loading && !data ? <Bone width={150} height={11} style={{ marginTop: 4 }}/> : status?.detail}</span>
              </span>
              <ArrowRightOutlined className="setting-card-arrow"/>
            </button>);
        })}
      </div>
    </>);
}
function ServerSettings() {
    const { data, error, loading } = useLoad(() => api('/v1/admin/settings'));
    const server = data?.server;
    return (<>
      <PageHeader title="Server" description="How this API is connected. These are read when it starts, before it can reach the database." actions={<BackToSettings />}/>
      <ErrorNotice error={error}/>
      <Panel title="Connection" description="Change these in the API's .env file and restart it.">
        {loading && !data ? (<div className="bone-lines" style={{ gap: 18 }} aria-busy>
            {[180, 240, 90].map((width, index) => (<div key={index} style={{ display: 'flex', gap: 24 }}>
                <Bone width={160} height={12}/>
                <Bone width={width} height={12}/>
              </div>))}
          </div>) : (<Descriptions column={1} colon={false} size="middle" styles={{ label: { width: 220, color: 'var(--muted)' } }}>
            <Descriptions.Item label="Database">{server?.database || 'In-memory (development)'}</Descriptions.Item>
            <Descriptions.Item label="Database host">{server?.databaseHost || '—'}</Descriptions.Item>
            <Descriptions.Item label="API port">{server?.port}</Descriptions.Item>
          </Descriptions>)}
      </Panel>
      <Panel title="Saved secrets" description="How keys entered in Settings are protected.">
        <p style={{ margin: 0, color: 'var(--body)', lineHeight: 1.6 }}>
          Keys entered in Settings (the B2 application key, the Anthropic API key, the Fast2SMS key, the WhatsApp API key, the Razorpay key and webhook secrets) are encrypted before they are saved in the database and are never shown again.
          The encryption key is <span className="mono">SETTINGS_KEY</span> in the API server's .env (or <span className="mono">data/settings.key</span> when that is not set), outside the database, so a copy of the database alone
          does not reveal them. Every server using this database needs the same key. Keep a copy somewhere safe; if it is lost, enter the keys again here.
        </p>
      </Panel>
    </>);
}
